import type { ReactNode } from 'react';
import { Flex, Stack, useMantineTheme } from '@mantine/core';

import { Text } from '~/domains/design-system/Text';

export type GameSection<T> = {
  key: string;
  title?: string;
  dateLabel?: string;
  headerActions?: ReactNode;
  items: T[];
};

export type GameSectionsContentProps<T> = {
  sections: GameSection<T>[];
  renderSectionItems: (
    items: T[],
    lastItemRef?: (node: HTMLDivElement | null) => void
  ) => ReactNode;
  lastItemRef?: (node: HTMLDivElement | null) => void;
  loadingMore?: ReactNode;
  footer?: ReactNode;
  fullWidth?: boolean;
};

export const GAME_SECTIONS_CONTENT_WIDTH = 700;

export function GameSectionsContent<T>({
  sections,
  renderSectionItems,
  lastItemRef,
  loadingMore,
  footer,
  fullWidth = false,
}: GameSectionsContentProps<T>) {
  const theme = useMantineTheme();
  const contentWidth = fullWidth ? '100%' : { base: '100%', md: GAME_SECTIONS_CONTENT_WIDTH };

  return (
    <>
      <Stack spacing={theme.other.spacing._24}>
        {sections.map((section, sectionIdx) => {
          const isLastSection = sectionIdx === sections.length - 1;

          return (
            <Flex key={section.key} direction="column" align="center" w="100%">
              <Stack w={contentWidth} maw="100%" spacing={0}>
                {section.dateLabel && (
                  <Text tag="p" variant="headline-xxl">
                    {section.dateLabel}
                  </Text>
                )}
                {(section.title || section.headerActions) && (
                  <Flex justify="space-between" align="center">
                    {section.title && (
                      <Text tag="p" variant="subhead-large">
                        {section.title}
                      </Text>
                    )}
                    {section.headerActions}
                  </Flex>
                )}
                {renderSectionItems(section.items, isLastSection ? lastItemRef : undefined)}
              </Stack>
            </Flex>
          );
        })}
      </Stack>
      {loadingMore && (
        <Flex direction="column" align="center" w="100%" pt={theme.other.spacing._24}>
          <Stack w={contentWidth} maw="100%" spacing={0}>
            {loadingMore}
          </Stack>
        </Flex>
      )}
      {footer && (
        <Flex direction="column" align="center" w="100%">
          <Stack w={contentWidth} maw="100%" spacing={0}>
            {footer}
          </Stack>
        </Flex>
      )}
    </>
  );
}
